/**
 * useListData Hook
 * Custom React hook for managing shopping list data with API integration
 */

import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../api/service';
import type { List, Item, CreateItemDto } from '../api/types';

interface UseListDataReturn {
  // State
  lists: List[];
  loading: boolean;
  error: string | null;
  
  // Item operations
  createItem: (data: CreateItemDto) => Promise<void>;
  updateItem: (id: number, data: Partial<Item>) => Promise<void>;
  toggleItem: (id: number) => Promise<void>;
  deleteItem: (id: number) => Promise<void>;
  
  // List operations
  createList: (name: string) => Promise<List>;
  deleteList: (id: number) => Promise<void>;
  
  // Utility
  refresh: () => Promise<void>;
}

/**
 * Custom hook for managing shopping list data
 * Provides CRUD operations with automatic refresh
 */
export function useListData(): UseListDataReturn {
  const [lists, setLists] = useState<List[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch all lists from API
   */
  const fetchLists = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getLists();
      setLists(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de chargement';
      setError(message);
      console.error('Error fetching lists:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Initial load
   */
  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  /**
   * Create a new item
   */
  const createItem = useCallback(async (data: CreateItemDto) => {
    try {
      setError(null);
      await apiService.createItem(data);
      await fetchLists(); // Refresh after creation
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la création';
      setError(message);
      throw err;
    }
  }, [fetchLists]);

  /**
   * Update an item
   */
  const updateItem = useCallback(async (id: number, data: Partial<Item>) => {
    try {
      setError(null);
      await apiService.updateItem(id, data);
      await fetchLists(); // Refresh after update
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la mise à jour';
      setError(message);
      throw err;
    }
  }, [fetchLists]);

  /**
   * Toggle item checked status
   */
  const toggleItem = useCallback(async (id: number) => {
    try {
      setError(null);
      // Find the item to get its current checked status
      const item = lists
        .flatMap(list => list.items || [])
        .find(i => i.id === id);
      
      if (!item) {
        throw new Error('Article non trouvé');
      }

      await apiService.updateItem(id, { checked: !item.checked });
      await fetchLists(); // Refresh after toggle
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la modification';
      setError(message);
      throw err;
    }
  }, [lists, fetchLists]);

  /**
   * Delete an item
   */
  const deleteItem = useCallback(async (id: number) => {
    try {
      setError(null);
      await apiService.deleteItem(id);
      await fetchLists(); // Refresh after deletion
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la suppression';
      setError(message);
      throw err;
    }
  }, [fetchLists]);

  /**
   * Create a new list
   * @returns The created list object with its ID
   */
  const createList = useCallback(async (name: string): Promise<List> => {
    try {
      setError(null);
      const newList = await apiService.createList({ name });
      await fetchLists(); // Refresh after creation
      return newList;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la création de la liste';
      setError(message);
      throw err;
    }
  }, [fetchLists]);

  /**
   * Delete a list
   */
  const deleteList = useCallback(async (id: number) => {
    try {
      setError(null);
      await apiService.deleteList(id);
      await fetchLists(); // Refresh after deletion
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la suppression de la liste';
      setError(message);
      throw err;
    }
  }, [fetchLists]);

  return {
    lists,
    loading,
    error,
    createItem,
    updateItem,
    toggleItem,
    deleteItem,
    createList,
    deleteList,
    refresh: fetchLists,
  };
}
